import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable, MotifButton } from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleItemsPerPageChange,
  handleGridReady,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
//import TenantServicesSelectCellRenderer from './customRenderers/TenantServicesSelectCellRenderer';
import ServiceNameCellEditor from './customCellEditor/ServiceNameCellEditor';
import TRSNameCellEditor from './customCellEditor/TRSNameCellEditor';
import CheckboxEditableGridCellRenderer from './customRenderers/CheckboxEditableGridCellRenderer';
import TenantServicesCellRenderer from './customRenderers/TenantServicesCellRenderer';
import OtherServiceNameCellEditor from './customCellEditor/OtherServiceNameCellEditor';
import {
  onCellEditingStarted,
  onCellEditingStopped,
  onCellValueChanged,
} from '../../common/AgGridEditableUtils';
import ErrorEditableGridCellRenderer from './customRenderers/ErrorEditableGridCellRenderer';

const TenantServicesTable = ({
  tenantServicesData,
  handleGridChanges,
  isEditModeActive,
  toggleGridEditMode,
  amenities,
  gridDataAfterAction,
  handleAddButtonClick,
  handleDeleteButtonClick,
  trsData,
  resetUpdatedRowDataTrigger,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [updatedRowData, setUpdatedRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Track whether the grid is currently being edited
  const [isGridEditing, setIsGridEditing] = useState(false);

  const getRowId = useCallback((params) => params.data.uniqueKey);
  const rowDataRef = useRef({});
  rowDataRef.current = rowData;

  // Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 150,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  // List of field names that should trigger the `onCellEditingStarted` event.
  // If we are using default ag grid checkbox component, we don't need to handle Cell editing started event for those checkbox fields.
  const fieldsToCheckForCellEditingStartedEvent = [
    'propertyAmenityName',
    'trsName',
    'serviceNameOtherDescription',
  ];

  // List of field names that should trigger the `onCellEditingStopped` event.
  const fieldsToCheckForCellEditingStoppedEvent = [
    'propertyAmenityName',
    'trsName',
    'serviceNameOtherDescription',
  ];

  // List of field names that should trigger the `onCellValueChanged` event.
  // The `onCellValueChanged` event is not triggered for Motif components.
  const fieldsToCheckForCellValueChangedEvent = [
    'isProvidedByEmployee',
    'isProvidedByIndependentContractor',
    'isProvidedByTaxableReitSubsidiary',
    'isProvidedByCustomary',
  ];

  // List of fields to update when Service Name is changed
  const fieldsToUpdateForServiceNameChange = [
    'isProvidedByCustomary',
    'isProvidedByEmployee',
    'isProvidedByIndependentContractor',
    'isProvidedByTaxableReitSubsidiary',
    'serviceNameOtherDescription',
    'trsName',
    'taxableREITSubsidiaryID',
  ];

  // Function to handle the grid editing status
  const handleGridEditingStatus = () => {
    const editingCells = gridRef?.current?.api?.getEditingCells() || [];
    setIsGridEditing(editingCells.length > 0);
  };

  // Function to determine if a cell has been modified compared to its original value.
  // It checks if the row is not in "add mode" and finds the corresponding row in `updatedRowData`.
  // If the original value differs from the current cell value, it returns true, triggering a visual indicator.
  const cellUpdatedIndicator = (params) => {
    // If `addMode` is true, return `true` immediately without additional checks
    if (params.data.addMode) {
      return true;
    }
    // Proceed with regular checks for updated values
    if (isEditModeActive && updatedRowData && updatedRowData.length > 0) {
      let matchedRow = updatedRowData.find(
        (row) => row.uniqueKey === params.data.uniqueKey
      );
      let columnName = 'original_' + params.colDef.field;
      if (
        matchedRow &&
        Object.prototype.hasOwnProperty.call(matchedRow, columnName) &&
        matchedRow[columnName] !== params.data[params.colDef.field]
      ) {
        return true;
      }
    }
    return false;
  };

  const getColumnDefs = () => {
    return [
      {
        field: 'reitTrackingID',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        minWidth: 225,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITEntityId'),
        },
      },
      {
        field: 'reitName',
        headerName: 'REIT Name',
        sortable: true,
        minWidth: 200,
        headerComponentParams: { template: CustomHeaderTemplate('ReitName') },
      },
      {
        field: 'propertyName',
        headerName: 'Property Name',
        sortable: true,
        minWidth: 250,
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyName'),
        },
      },
      {
        field: 'propertyTrackingID',
        headerName: 'REITSuite Entity ID for Property',
        sortable: true,
        minWidth: 225,
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyEntityId'),
        },
      },
      {
        field: 'propertyTypeDescription',
        headerName: 'Property Type',
        sortable: true,
        minWidth: 200,
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyType'),
        },
      },
      {
        field: 'propertyAmenityName',
        headerName: 'Service Name',
        sortable: true,
        minWidth: 265,
        headerComponentParams: {
          template: CustomHeaderTemplate('ServiceName'),
        },
        cellRenderer: ErrorEditableGridCellRenderer,
        cellRendererParams: { isEditModeActive },
        editable: isEditModeActive ? true : false,
        cellEditor: ServiceNameCellEditor,
        cellEditorParams: {
          options: amenities.options.map((option) => ({
            key: option.value,
            value: option.value,
            text: option.label,
          })),
          handleServiceNameChange: (value, uniqueKey, columnName) => {
            // Sync the data from rowData to updatedRowData and update Service Name
            const mergedArray = syncRowDataToUpdatedRowData();

            // Now update Service Name for selected Service
            const modifiedData = mergedArray.map((obj) => {
              if (obj.uniqueKey === uniqueKey) {
                const updatedFields = {};

                fieldsToUpdateForServiceNameChange.forEach((field) => {
                  const originalField = `original_${field}`;

                  // Only set original_ field if it doesn't already exist
                  if (
                    !Object.prototype.hasOwnProperty.call(obj, originalField)
                  ) {
                    updatedFields[originalField] = obj[field];
                  }

                  // Reset the actual field
                  if (field === 'taxableREITSubsidiaryID') {
                    updatedFields[field] = -1;
                  } else if (
                    field === 'trsName' ||
                    field === 'serviceNameOtherDescription'
                  ) {
                    updatedFields[field] = '';
                  } else {
                    updatedFields[field] = false;
                  }
                });

                // Apply the new value to the specified column
                return {
                  ...obj,
                  ...updatedFields,
                  [columnName]: value,
                };
              }
              return obj;
            });

            setUpdatedRowData(modifiedData);
          },
        },
        cellClassRules: {
          'editable-cell-indicator': () => isEditModeActive,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'serviceNameOtherDescription',
        headerName: 'Service Name - Other Description',
        sortable: true,
        minWidth: 265,
        headerComponentParams: {
          template: CustomHeaderTemplate(
            'PropertyServiceName_OtherDescription'
          ),
        },
        cellRenderer: ErrorEditableGridCellRenderer,
        cellRendererParams: { isEditModeActive },
        editable: (params) =>
          isEditModeActive && params.data.propertyAmenityName == 'Other'
            ? true
            : false,
        cellEditorSelector: (params) => {
          if (params.data.propertyAmenityName === 'Other') {
            return {
              component: OtherServiceNameCellEditor,
              params: {
                handleOtherServiceNameChange: (
                  otherServiceName,
                  uniqueKey,
                  columnName
                ) => {
                  // Update other service name in updatedRowData
                  let modifiedData =
                    updatedRowData && updatedRowData.length > 0
                      ? JSON.parse(JSON.stringify(updatedRowData))
                      : [];
                  let matchedRow = modifiedData.find(
                    (row) => row.uniqueKey === uniqueKey
                  );
                  if (matchedRow) {
                    matchedRow[columnName] = otherServiceName;
                    setUpdatedRowData(modifiedData);
                  }
                },
              },
            };
          } else {
            return null;
          }
        },
        cellClassRules: {
          'editable-cell-indicator': (params) =>
            isEditModeActive && params.data.propertyAmenityName === 'Other',
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'isProvidedByCustomary',
        headerName: 'Customary',
        sortable: true,
        minWidth: 179,
        headerComponentParams: { template: CustomHeaderTemplate('Customary') },
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        editable: isEditModeActive ? true : false,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        cellClassRules: {
          'editable-cell-indicator': () => isEditModeActive,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'isProvidedByEmployee',
        headerName: 'Employee',
        sortable: true,
        minWidth: 172,
        headerComponentParams: { template: CustomHeaderTemplate('Employee') },
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        editable: isEditModeActive ? true : false,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        cellClassRules: {
          'editable-cell-indicator': () => isEditModeActive,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'isProvidedByIndependentContractor',
        headerName: 'IK',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('IK') },
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        editable: isEditModeActive ? true : false,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        cellClassRules: {
          'editable-cell-indicator': () => isEditModeActive,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'isProvidedByTaxableReitSubsidiary',
        headerName: 'TRS',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('TRS') },
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        editable: isEditModeActive ? true : false,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        cellClassRules: {
          'editable-cell-indicator': () => isEditModeActive,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
        onCellClicked: (params) => {
          // onCellClicked is called when checkbox is selected/deselected and grid column is already in edit mode
          // When checkbox is checked then set focus to TRS Name column
          if (
            params.event.target.tagName === 'INPUT' &&
            params.event.target.type === 'checkbox' &&
            params.event.target.checked === true
          ) {
            params.api.startEditingCell({
              rowIndex: params.rowIndex,
              colKey: 'trsName',
            });
          }
        },
      },
      {
        field: 'trsName',
        headerName: 'TRS Name',
        sortable: true,
        minWidth: 265,
        headerComponentParams: { template: CustomHeaderTemplate('TRSName') },
        cellRenderer: ErrorEditableGridCellRenderer,
        cellRendererParams: { isEditModeActive },
        editable: (params) =>
          isEditModeActive &&
          params.data.isProvidedByTaxableReitSubsidiary == true
            ? true
            : false,
        cellEditorSelector: (params) => {
          if (params.data.isProvidedByTaxableReitSubsidiary == true) {
            return {
              component: TRSNameCellEditor,
              params: {
                options: trsData
                  .filter((option) => option.reitid === params.data.reitid)
                  .map((option) => ({
                    key: option.taxableREITSubsidiaryID,
                    value: option.trsName,
                    text: option.trsName,
                  })),
                handleTRSNameChange: (value, uniqueKey, columnName) => {
                  // Get TRS Id of selected TRS Name
                  const taxableREITSubsidiaryId =
                    trsData.find(
                      (item) =>
                        item.reitid === params.data.reitid &&
                        item.trsName === value
                    )?.taxableREITSubsidiaryID || -1;
                  // Sync the data from rowData to updatedRowData and update Service Name
                  const mergedArray = syncRowDataToUpdatedRowData();
                  // Now update TRS Name for selected TRS
                  const modifiedData = mergedArray.map((obj) => {
                    if (obj.uniqueKey === uniqueKey) {
                      return {
                        ...obj,
                        [columnName]: value,
                        taxableREITSubsidiaryID: taxableREITSubsidiaryId, // Update taxableREITSubsidiary Id
                      };
                    }
                    return obj;
                  });
                  setUpdatedRowData(modifiedData);
                },
              },
            };
          } else {
            return null;
          }
        },

        cellClassRules: {
          'editable-cell-indicator': (params) =>
            isEditModeActive &&
            params.data.isProvidedByTaxableReitSubsidiary === true,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
      {
        field: 'addactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        resizable: false,
        hide: !isEditModeActive ? true : false,
        cellRenderer: (params) => {
          if (!isEditModeActive) {
            return null;
          }
          return <TenantServicesCellRenderer {...params} />;
        },
        cellRendererParams: { handleAddButtonClick, gridRef },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        resizable: false,
        hide: !isEditModeActive ? true : false,
        cellRenderer: (params) => {
          if (!isEditModeActive) {
            return null;
          }
          return <TenantServicesCellRenderer {...params} />;
        },
        cellRendererParams: { handleDeleteButtonClick, gridRef },
      },
    ];
  };

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData && rowData.length > 0 && gridRef?.current?.api !== undefined) {
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (calcTotalPages === 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    const clonedData = JSON.parse(JSON.stringify(tenantServicesData));
    setRowData(clonedData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [tenantServicesData]);

  useEffect(() => {
    setTimeout(function () {
      if (gridDataAfterAction && gridDataAfterAction.length > 0) {
        setRowData(gridDataAfterAction);
        const mergedArray = syncRowDataToUpdatedRowData(gridDataAfterAction);
        setUpdatedRowData(mergedArray);
      }
    }, 0);
  }, [gridDataAfterAction]);

  useEffect(() => {
    // Reset all the tracked changes of row data
    setUpdatedRowData([]);
  }, [resetUpdatedRowDataTrigger]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    gridRef.current.api.paginationGoToPage(pageNumber - 1);
  };

  const onCellKeyDown = (event) => {
    if (event.event.key === 'Enter' || event.event.key === ' ') {
      if (event.colDef.field == 'editactions') {
        handleAddButtonClick(event.data);
      }
      if (event.colDef.field == 'deleteactions') {
        handleDeleteButtonClick(event.data);
      }
    }
  };

  /**
   * Validates the 'serviceNameOtherDescription' field for a given row item.
   *
   * - Applies only when 'propertyAmenityName' is set to 'Other'.
   * - Ensures the field is not empty or whitespace-only.
   * - Ensures the field does not exceed 255 characters.
   *
   * @param {Object} item - The row item to validate.
   * @returns {string} - Returns an error message if validation fails, otherwise an empty string
   */
  const validateServiceNameOtherDescription = (item) => {
    if (item.propertyAmenityName === 'Other') {
      if (!item.serviceNameOtherDescription?.trim()) {
        return 'Please enter Service Name Other Description.';
      }
      if (item.serviceNameOtherDescription?.trim().length > 255) {
        return 'Maximum 255 characters allowed.';
      }
    }
    return '';
  };

  /**
   * Validates all rows in `rowData` by checking required fields and enforcing uniqueness.
   *
   * - Iterates over `rowData`, applying `_error` fields where necessary.
   * - Ensures `propertyAmenityName` is unique within each `propertyID`.
   * - Determines if all `_error` fields are empty, meaning validation passes.
   * - Updates `rowData` if errors are found.
   *
   * @returns {boolean} - `true` if all rows are valid, otherwise `false`
   */
  const validateAllRowData = () => {
    // Create a map to track amenity names per propertyID
    const propertyAmenityTracker = new Map();
    const otherDescriptionTracker = new Map();

    // Count how many entries exist for each propertyID
    const propertyIDCounts = rowData.reduce((acc, item) => {
      acc[item.propertyID] = (acc[item.propertyID] || 0) + 1;
      return acc;
    }, {});

    const updatedArray = rowData.map((item) => {
      const errors = {
        ...item,
        propertyAmenityName_error: !item.propertyAmenityName
          ? 'Please select Service Name.'
          : '',
        serviceNameOtherDescription_error:
          validateServiceNameOtherDescription(item),
      };

      // Check if only one record exists for this propertyID
      // If there's only one record for the property AND:
      // - propertyAmenityName is empty
      // - all provider flags are false
      // Then clear the propertyAmenityName_error field (no error needed in this edge case)
      if (
        propertyIDCounts[item.propertyID] === 1 &&
        !item.propertyAmenityName &&
        !item.isProvidedByCustomary &&
        !item.isProvidedByEmployee &&
        !item.isProvidedByIndependentContractor &&
        !item.isProvidedByTaxableReitSubsidiary
      ) {
        errors.propertyAmenityName_error = '';
      }

      // Uniqueness check for propertyAmenityName
      if (item.propertyAmenityName && item.propertyAmenityName !== 'Other') {
        if (!propertyAmenityTracker.has(item.propertyID)) {
          propertyAmenityTracker.set(item.propertyID, new Map());
        }

        const amenityMap = propertyAmenityTracker.get(item.propertyID);
        if (amenityMap.has(item.propertyAmenityName)) {
          errors.propertyAmenityName_error = `Duplicate service name exists for property ${item.propertyName}`;
          amenityMap.get(item.propertyAmenityName).propertyAmenityName_error =
            errors.propertyAmenityName_error;
        } else {
          amenityMap.set(item.propertyAmenityName, errors);
        }
      }

      // Uniqueness check for serviceNameOtherDescription
      if (
        item.propertyAmenityName === 'Other' &&
        item.serviceNameOtherDescription &&
        item.serviceNameOtherDescription?.trim() !== '' &&
        item.serviceNameOtherDescription?.trim().length <= 255
      ) {
        if (!otherDescriptionTracker.has(item.propertyID)) {
          otherDescriptionTracker.set(item.propertyID, new Map());
        }

        const descriptionMap = otherDescriptionTracker.get(item.propertyID);
        const normalizedDescription = item.serviceNameOtherDescription
          .trim()
          .toLowerCase();

        if (descriptionMap.has(normalizedDescription)) {
          errors.serviceNameOtherDescription_error = `Duplicate service name - other description exists for property ${item.propertyName}`;
          descriptionMap.get(
            normalizedDescription
          ).serviceNameOtherDescription_error =
            errors.serviceNameOtherDescription_error;
        } else {
          descriptionMap.set(normalizedDescription, errors);
        }
      }

      return errors;
    });

    // Validate if there are any error messages
    const isValid = updatedArray.every((item) =>
      Object.keys(item).every(
        (key) => !key.endsWith('_error') || item[key] === ''
      )
    );

    if (!isValid) {
      setRowData(updatedArray);
    }

    return isValid;
  };

  /**
   * Synchronizes 'rowData' to 'updatedRowData', maintaining order and integrity.
   *
   * - Uses `sourceData` if provided as input parameter; otherwise, defaults to `rowData`.
   * - Creates a copy of `updatedRowData` if available, or initializes an empty array.
   * - Iterates through `updatedRowData` to find matching entries in the reference dataset using `uniqueKey`.
   * - Merges matching objects, copying all fields except those that start with "original".
   * - Ensures missing rows from `rowData` or `sourceData` are inserted at their correct positions.
   * - Returns the updated array with synchronized data while preserving row order.
   *
   * If no match is found, the existing item from `updatedRowData` remains unchanged.
   *
   * @param {Array} [sourceData=[]] - Optional dataset used for synchronization; defaults to `rowData` if empty.
   * @returns {Array} Updated row data array with merged values and correctly positioned missing rows.
   */
  const syncRowDataToUpdatedRowData = (sourceData = []) => {
    // Determine the source data to use
    const referenceData =
      Array.isArray(sourceData) && sourceData.length > 0 ? sourceData : rowData;

    // Convert updatedRowData into a Map for quick lookups
    const updatedRowMap = new Map(
      Array.isArray(updatedRowData)
        ? updatedRowData.map((row) => [row?.uniqueKey, row])
        : []
    );

    // Process referenceData to ensure all missing rows are inserted at the correct positions
    const mergedArray = referenceData.map((firstItem) => {
      if (updatedRowMap.has(firstItem.uniqueKey)) {
        const secondItem = updatedRowMap.get(firstItem.uniqueKey);

        // Copy fields while excluding "original" fields
        const filteredFields = Object.keys(firstItem).reduce((acc, key) => {
          if (!key.startsWith('original')) {
            acc[key] = firstItem[key];
          }
          return acc;
        }, {});

        return { ...secondItem, ...filteredFields };
      }

      return firstItem; // If not found in updatedRowData, keep it as-is
    });

    return mergedArray; // Preserves referenceData order while merging updates from updatedRowData
  };

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div className="ag-theme-quartz motif-table table-bd-custom-height-width-tenant-service">
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={getColumnDefs()}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRendered(gridRef)}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged(
                gridRef,
                currentPage,
                setCurrentPage,
                setTotalPages
              )}
              data-testid="grdlctrBulkTenantServices/AmenitiesData"
              onCellKeyDown={onCellKeyDown}
              onCellEditingStarted={(event) =>
                onCellEditingStarted(
                  event,
                  fieldsToCheckForCellEditingStartedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData,
                  handleGridEditingStatus
                )
              }
              onCellEditingStopped={(event) =>
                onCellEditingStopped(
                  event,
                  fieldsToCheckForCellEditingStoppedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData,
                  setRowData,
                  handleGridEditingStatus
                )
              }
              onCellValueChanged={(event) =>
                onCellValueChanged(
                  event,
                  fieldsToCheckForCellValueChangedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData,
                  setRowData
                )
              }
              singleClickEdit={true}
              getRowId={getRowId}
            />
            <div className="mt-3">
              {isEditModeActive && (
                <React.Fragment>
                  <span className="right mb-3">
                    <MotifButton
                      variant="primary"
                      type="button"
                      data-testid="btnSave"
                      disabled={isGridEditing}
                      onClick={() => {
                        gridRef?.current?.api?.clearFocusedCell();
                        setTimeout(() => {
                          if (validateAllRowData()) {
                            toggleGridEditMode('save', gridRef?.current?.api);
                          }
                        }, 1000); // Ensures this executes after the clearFocusedCell() UI change
                      }}
                      style={{ maxHeight: '3rem' }}
                    >
                      Save
                    </MotifButton>
                  </span>
                  <span className="right mb-3 mr-3 ml-5">
                    <MotifButton
                      variant="secondary"
                      type="button"
                      data-testid="btnCancel"
                      onClick={() => {
                        gridRef?.current?.api?.stopEditing();
                        gridRef?.current?.api?.clearFocusedCell();
                        setTimeout(() => {
                          toggleGridEditMode('cancel', gridRef?.current?.api);
                        }, 100); // Ensures this executes after the clearFocusedCell() UI change
                      }}
                      style={{ maxHeight: '3rem' }}
                    >
                      Cancel
                    </MotifButton>
                  </span>
                </React.Fragment>
              )}
              {rowData.length > 0 && (
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

TenantServicesTable.propTypes = {
  tenantServicesData: PropTypes.array.isRequired,
  handleGridChanges: PropTypes.func.isRequired,
  gridColumnState: PropTypes.object,
  handleGridColumnStateChange: PropTypes.func.isRequired,
  isEditModeActive: PropTypes.bool.isRequired,
  toggleGridEditMode: PropTypes.func.isRequired,
  amenities: PropTypes.object.isRequired,
  gridDataAfterAction: PropTypes.object.isRequired,
  handleAddButtonClick: PropTypes.func.isRequired,
  handleDeleteButtonClick: PropTypes.func.isRequired,
  trsData: PropTypes.object.isRequired,
  resetUpdatedRowDataTrigger: PropTypes.number.isRequired,
};

export default TenantServicesTable;
